import { get, ref, set } from "firebase/database";
import { addNotification, updateNotification } from "./alerts";
import { createCreativeJobAndWaitForResponse } from "./jobs";
import { promoteCreativeJobNotification } from "./creativeJobs";
import { getPathDatabaseByRole, renewToken } from "./auth";
import { db } from "../lib/firebase";
import axios from "../lib/axios";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import { getUniqueID } from "../utils/numbers";
import { ALERTS_ASYNC_STATUS } from "../utils/constants";
import { CREATIVE_JOB_STATUS } from "../utils/creativeJobStatus";
import { CREATIVE_JOB_TYPES, LaunchError } from "../utils/errors";
import { replaceUndefinedWithNull } from "../utils/forms";
import { hashStringToID } from "../utils/string";
import {
  getCreativeJobAlertMessageKey,
  mapJobResultToGenerationData,
} from "../utils/creativeJobsNotifications";

const ANALYZE_IMAGE_LAYERS_TIMEOUT_MS = 9 * 60 * 1000;

export const getCreativesLayerizedPath = ({ advertiserID, urlHash }) =>
  `creativesLayerized/${advertiserID}/${urlHash}`;

export const startGetCreativesLayerized =
  ({ sourceUrl }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const trimmedUrl = String(sourceUrl || "").trim();
      if (!advertiserID || !trimmedUrl) {
        return { ok: false, data: null };
      }

      const urlHash = hashStringToID(trimmedUrl);
      const snapshot = await get(
        ref(db, getCreativesLayerizedPath({ advertiserID, urlHash }))
      );

      if (!snapshot.exists()) {
        return { ok: true, data: null, urlHash };
      }

      return { ok: true, data: snapshot.val(), urlHash };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetCreativesLayerized",
        })
      );
      return { ok: false, data: null };
    }
  };

export const startSaveCreativesLayerized =
  ({ sourceUrl, snapshot }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const trimmedUrl = String(sourceUrl || "").trim();
      if (!advertiserID || !trimmedUrl || !snapshot) {
        return { ok: false };
      }

      const urlHash = hashStringToID(trimmedUrl);
      const now = Date.now();
      await set(
        ref(db, getCreativesLayerizedPath({ advertiserID, urlHash })),
        replaceUndefinedWithNull({
          ...snapshot,
          sourceUrl: trimmedUrl,
          updateTime: now,
          creationTime: snapshot.creationTime || now,
        })
      );

      return { ok: true, urlHash };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveCreativesLayerized",
        })
      );
      return { ok: false };
    }
  };

export const startEraseLayerObject =
  ({ imageUrl, maskUrl, preserveAlpha = false, layerId = null }) =>
  async (dispatch) => {
    try {
      return await createCreativeJobAndWaitForResponse({
        dispatch,
        jobType: CREATIVE_JOB_TYPES.eraseLayerObject,
        payload: {
          imageUrl: String(imageUrl || "").trim(),
          maskUrl: String(maskUrl || "").trim(),
          preserveAlpha: Boolean(preserveAlpha),
          ...(layerId ? { layerId } : {}),
        },
      });
    } catch (error) {
      return dispatch(
        LaunchError(error, false, [], {
          functionName: "startEraseLayerObject",
          creativeJobType: CREATIVE_JOB_TYPES.eraseLayerObject,
        })
      );
    }
  };

export const startAnalyzeImageLayers =
  ({
    url,
    contentID,
    isCarousel = false,
    carouselIndex,
    extractText = false,
    onJobCreated = () => {},
  }) =>
  async (dispatch) => {
    const alertID = getUniqueID();
    let notificationId = alertID;
    const sourceUrl = String(url || "").trim();
    const payloadSnapshot = {
      modalType: "imageEditLayers",
      url: sourceUrl,
      sourceUrl,
      extractText: Boolean(extractText),
      ...(contentID ? { contentID } : {}),
      ...(isCarousel && carouselIndex !== undefined && carouselIndex !== null
        ? { isCarousel: true, carouselIndex }
        : {}),
    };

    try {
      dispatch(
        addNotification({
          type: "analyzingImageLayers",
          id: alertID,
          status: ALERTS_ASYNC_STATUS.LOADING,
          progress: 0,
          jobType: CREATIVE_JOB_TYPES.analyzeImageLayers,
          messageKey: getCreativeJobAlertMessageKey({
            jobType: CREATIVE_JOB_TYPES.analyzeImageLayers,
          }),
          generationData: {
            ...payloadSnapshot,
            isGenerating: true,
          },
        })
      );

      const data = await createCreativeJobAndWaitForResponse({
        dispatch,
        jobType: CREATIVE_JOB_TYPES.analyzeImageLayers,
        timeoutMs: ANALYZE_IMAGE_LAYERS_TIMEOUT_MS,
        payload: {
          url: sourceUrl,
          extractText: Boolean(extractText),
          ...(contentID ? { contentID } : {}),
          ...(isCarousel &&
          carouselIndex !== undefined &&
          carouselIndex !== null
            ? { isCarousel: true, carouselIndex }
            : {}),
        },
        onJobCreated: ({ jobID }) => {
          dispatch(
            promoteCreativeJobNotification({ fromId: notificationId, jobID })
          );
          notificationId = jobID;
          onJobCreated({ jobID });
        },
      });

      dispatch(
        updateNotification(notificationId, {
          status: ALERTS_ASYNC_STATUS.SUCCESS,
          progress: 100,
          generationData: mapJobResultToGenerationData({
            job: {
              jobType: CREATIVE_JOB_TYPES.analyzeImageLayers,
              status: CREATIVE_JOB_STATUS.SUCCESS,
              response: data,
            },
            payloadSnapshot,
          }),
        })
      );

      return data;
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          creativeJobType: CREATIVE_JOB_TYPES.analyzeImageLayers,
          functionName: "startAnalyzeImageLayers",
        })
      );

      dispatch(
        updateNotification(notificationId, {
          status: ALERTS_ASYNC_STATUS.ERROR,
          message: errorFormatted.message,
          progress: 100,
          generationData: {
            ...payloadSnapshot,
            isGenerating: false,
          },
        })
      );

      return errorFormatted;
    }
  };

export const startSaveEditedLayeredImage =
  ({ layers, canvasWidth, canvasHeight }) =>
  async (dispatch) => {
    try {
      if (!Array.isArray(layers) || !layers.length) {
        return { ok: false, reason: "missingParameters" };
      }
      const width = Number(canvasWidth);
      const height = Number(canvasHeight);
      if (!(width > 0) || !(height > 0)) {
        return { ok: false, reason: "missingParameters" };
      }
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "saveEditedLayeredImage"
        )}/saveEditedLayeredImage`,
        data: {
          layers: JSON.parse(JSON.stringify(layers)),
          canvasWidth: width,
          canvasHeight: height,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!result?.ok) {
        return { ok: false, message: result?.message };
      }
      return { ok: true, data: result.data };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveEditedLayeredImage",
          tags: { action: "imageLayers" },
          level: "error",
        })
      );
      return { ok: false, reason: "saveFailed", message: error?.message };
    }
  };
